import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import {
  apiFetchPartnerAutoAssign,
  apiTogglePartnerAutoAssign,
  apiUpdatePartnerAutoAssignSettings,
} from "../../api/index.js";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog";
import {
  colors,
  font,
  text,
  card,
  cardBody,
  grid,
  inp,
  button,
  pill,
} from "./partnerTheme";
/* The tooltips on this screen come from the Settings help registry
   (views/Settings/help/vendorSettingsHelp.js), where they were checked against
   services/autoAssignmentService.js on 1 Nov 2026: the strategy is stored but
   every offer still goes to the closest eligible rider, and the radius widens
   once to Riders.MAX_ASSIGN_RADIUS_KM when nobody is inside it. */
import SettingHelp from "../Settings/settingsHelp";
import AppSelect from "../../components/ui/AppSelect";

const serverMessage = (err, fallback) =>
  err?.response?.data?.error?.message || fallback;

/*
 * These mirror the defaults declared on `autoAssignSettings` in
 * ThreePLPartnerModel. They are shown as help text rather than used to
 * pre-fill, because a partner who has never opened this screen is already
 * running on them, and the useful question is "how far am I from the
 * default", not "what would a blank field do".
 */
const DEFAULTS = {
  strategy: "proximity",
  maxRadiusKm: 10,
  maxRetries: 3,
  cooldownSeconds: 5,
  autoRejectTimeoutSeconds: 60,
};

/* The enum is fixed in the model. Anything not in this list is rejected by
   mongoose on save, so the field is a select and never free text. */
const STRATEGIES = [
  {
    value: "proximity",
    label: "Closest rider",
    effect: "Offers the job to the free rider nearest the pickup point who is allowed to take it.",
  },
  {
    value: "performance",
    label: "Best performing rider (not applied yet)",
    effect:
      "Saved for later. Offers still go to the closest eligible rider until this option is built.",
  },
  {
    value: "round_robin",
    label: "Round robin (not applied yet)",
    effect:
      "Saved for later. Offers still go to the closest eligible rider until this option is built.",
  },
];

/* Every numeric setting the server actually accepts. `updateAutoAssignSettings`
   filters the body down to exactly these keys, so sending anything else is
   silently dropped and would make this form lie about what it saved. */
const NUMERIC_FIELDS = [
  {
    key: "maxRadiusKm",
    topic: "partnerAutoAssignRadius",
    label: "Search radius",
    unit: "km",
    min: 1,
    effect:
      "How far from the pickup point Lusuku looks first for one of your riders. If none is free inside it, the search widens once to Lusuku's fleet-wide limit.",
  },
  {
    key: "maxRetries",
    topic: "partnerAutoAssignRetries",
    label: "Riders to try",
    unit: "attempts",
    min: 1,
    effect:
      "How many of your riders are offered the job in turn before the system stops trying and the order falls back to manual assignment.",
  },
  {
    key: "cooldownSeconds",
    topic: "partnerAutoAssignCooldown",
    label: "Cooldown before offering",
    unit: "seconds",
    min: 0,
    effect:
      "The pause between the order arriving and the first automatic offer. This is the window in which one of your dispatchers can still take the job and assign it by hand.",
  },
  {
    key: "autoRejectTimeoutSeconds",
    topic: "partnerAutoRejectTimeout",
    label: "Rider response time",
    unit: "seconds",
    min: 10,
    effect:
      "How long a rider has to accept before the offer expires and moves to the next rider.",
  },
];

const Field = ({ label, help, effect, children }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={text.label}>{label}</label>
    {children}
    <p style={{ ...text.body, fontSize: 12, margin: "6px 0 0" }}>{effect}</p>
    <p style={text.help}>{help}</p>
  </div>
);

const PartnerAutoAssign = () => {
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const operationMode = user?.result?.operationMode;
  const isPartnerAccount = operationMode === "3PL_PARTNER";
  const permissions = user?.result?.permissions || {};

  /* The routes behind this screen sit on the "riders" permission: view to read
     the status, full to toggle or save. The server re-checks both and answers
     403, so this is not the security boundary. It only stops the screen
     offering a button whose one outcome would be a refusal. */
  const canEdit = isPartnerAccount || permissions?.riders === "full";

  const confirmDialog = useConfirmDialog();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);

  /* The toggle route answers with only enabled, strategy and lastToggled, so
     merging rather than replacing keeps the radius and timers on screen
     instead of blanking them after a toggle. */
  const applyStatus = useCallback((incoming) => {
    if (!incoming) return;
    setStatus((prev) => ({ ...(prev || {}), ...incoming }));
  }, []);

  const load = useCallback(async () => {
    if (!partnerId) {
      setLoading(false);
      setError(
        "No partner account is attached to this login, so there is no dispatch automation to show."
      );
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await apiFetchPartnerAutoAssign(partnerId);
      const incoming = res?.data?.autoAssign;
      if (res?.data?.success && incoming) {
        setStatus(incoming);
        setForm({
          strategy: incoming.strategy ?? DEFAULTS.strategy,
          maxRadiusKm: incoming.maxRadiusKm ?? DEFAULTS.maxRadiusKm,
          maxRetries: incoming.maxRetries ?? DEFAULTS.maxRetries,
          cooldownSeconds: incoming.cooldownSeconds ?? DEFAULTS.cooldownSeconds,
          autoRejectTimeoutSeconds:
            incoming.autoRejectTimeoutSeconds ?? DEFAULTS.autoRejectTimeoutSeconds,
        });
      } else {
        setError("Automatic dispatch settings could not be loaded.");
      }
    } catch (err) {
      setError(serverMessage(err, "Automatic dispatch settings could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const enabled = !!status?.enabled;

  const runToggle = async (next) => {
    try {
      setToggling(true);
      const res = await apiTogglePartnerAutoAssign(partnerId, next);
      if (res?.data?.success) {
        applyStatus(res.data.autoAssign);
        toast.success(
          next
            ? "Automatic dispatch is on. New orders will be offered to your riders."
            : "Automatic dispatch is off. Every order now waits for a person to assign it."
        );
      } else {
        toast.error("The change was not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The change was not saved."));
    } finally {
      setToggling(false);
    }
  };

  const handleToggle = () => {
    if (!canEdit) {
      toast.error("You do not have permission to change dispatch automation.");
      return;
    }
    /* Turning it on only widens what the system may do, and it is reversible
       from this same button. Turning it off silently stops orders reaching
       riders, and nobody finds out until jobs sit unassigned, so only that
       direction is worth interrupting for. */
    if (enabled) {
      confirmDialog.ask({
        title: "Turn off automatic dispatch?",
        body:
          "New orders will stop being offered to your riders on their own. From then on every order sits until one of your dispatchers assigns it by hand.",
        note: "You can turn this back on at any time from this screen.",
        tone: "warning",
        confirmLabel: "Turn it off",
        busyLabel: "Turning off",
        onConfirm: () => runToggle(false),
      });
      return;
    }
    runToggle(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const saveSettings = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to change dispatch automation.");
      return;
    }

    const payload = { strategy: form.strategy };
    for (const field of NUMERIC_FIELDS) {
      const value = Number(form[field.key]);
      if (!Number.isFinite(value) || value < field.min) {
        toast.error(`${field.label} has to be a number of ${field.min} or more.`);
        return;
      }
      payload[field.key] = value;
    }

    try {
      setSaving(true);
      const res = await apiUpdatePartnerAutoAssignSettings(partnerId, payload);
      const saved = res?.data?.autoAssignSettings;
      if (res?.data?.success && saved) {
        applyStatus(saved);
        toast.success("Dispatch settings saved.");
      } else {
        toast.error("The settings were not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The settings were not saved."));
    } finally {
      setSaving(false);
    }
  };

  const lastToggled = status?.lastToggled ? new Date(status.lastToggled) : null;
  const lastToggledReadable =
    lastToggled && !Number.isNaN(lastToggled.getTime())
      ? lastToggled.toLocaleString()
      : null;

  const activeStrategy = STRATEGIES.find((s) => s.value === form.strategy);

  if (loading) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, textAlign: "center", color: colors.muted }}>
          <p style={{ ...text.body, margin: 0 }}>Loading dispatch settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={{ fontFamily: font.sans, background: colors.ground }}>
      <div style={{ marginBottom: 20 }}>
        <h4 style={text.title}>Automatic dispatch</h4>
        <p style={text.subtitle}>
          Whether Lusuku offers new orders to your riders on its own, and the rules it
          follows when it does.
        </p>
      </div>

      {error ? (
        <div
          style={{
            ...card,
            ...cardBody,
            background: colors.redSoft,
            borderColor: colors.red,
          }}
        >
          <p style={{ ...text.body, color: colors.red, margin: 0 }}>{error}</p>
        </div>
      ) : null}

      {status ? (
        <>
          <div style={card}>
            <div style={cardBody}>
              <p style={text.section}>
                Current state
                <SettingHelp topic="partnerAutoDispatch" />
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <span style={pill(enabled ? "good" : "warn")}>
                  {enabled ? "On" : "Off"}
                </span>
                <span style={{ ...text.body, fontWeight: 600, color: colors.ink }}>
                  {enabled
                    ? "Orders are offered to your riders automatically."
                    : "Every order waits for a person to assign it."}
                </span>
              </div>

              <p style={{ ...text.body, margin: "0 0 6px", maxWidth: 720 }}>
                {enabled
                  ? "When an order is ready for you, Lusuku waits out the cooldown below and then offers it to your closest eligible rider, then the next, one at a time. Your dispatchers can still assign by hand during the cooldown, and an order nobody accepts waits for them."
                  : "Nothing is sent to your riders on its own. An order stays in your queue until one of your dispatchers picks a rider for it, however long that takes."}
              </p>

              {lastToggledReadable ? (
                <p style={{ ...text.help, margin: "10px 0 0" }}>
                  Last changed {lastToggledReadable}
                </p>
              ) : null}

              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  style={button(enabled ? "danger" : "primary", {
                    opacity: !canEdit || toggling ? 0.6 : 1,
                    cursor: !canEdit || toggling ? "not-allowed" : "pointer",
                  })}
                  disabled={!canEdit || toggling}
                  onClick={handleToggle}
                >
                  {toggling
                    ? "Working..."
                    : enabled
                    ? "Turn off automatic dispatch"
                    : "Turn on automatic dispatch"}
                </button>
                {!canEdit ? (
                  <p style={{ ...text.help, margin: "8px 0 0" }}>
                    Only the partner account owner, or staff with full rider access, can
                    change this.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div style={card}>
            <div style={cardBody}>
              <p style={text.section}>Dispatch rules</p>
              <p style={{ ...text.body, margin: "0 0 20px", maxWidth: 720 }}>
                These rules are saved whether automatic dispatch is on or off, and they
                take effect the moment it is on.
              </p>

              <Field
                label={
                  <>
                    How a rider is chosen
                    <SettingHelp topic="partnerAutoAssignStrategy" />
                  </>
                }
                effect={activeStrategy ? activeStrategy.effect : ""}
                help={`Default: ${
                  STRATEGIES.find((s) => s.value === DEFAULTS.strategy).label
                }`}
              >
                <AppSelect
                  {...inp()}
                  value={form.strategy}
                  disabled={!canEdit}
                  onChange={(e) => setField("strategy", e.target.value)}
                >
                  {STRATEGIES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </AppSelect>
              </Field>

              <div style={grid(2)}>
                {NUMERIC_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={
                      <>
                        {`${field.label} (${field.unit})`}
                        <SettingHelp topic={field.topic} />
                      </>
                    }
                    effect={field.effect}
                    help={`Default: ${DEFAULTS[field.key]} ${field.unit}`}
                  >
                    <input
                      {...inp()}
                      type="number"
                      min={field.min}
                      value={form[field.key]}
                      disabled={!canEdit}
                      onChange={(e) => setField(field.key, e.target.value)}
                    />
                  </Field>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button
                  type="button"
                  style={button("primary", {
                    opacity: !canEdit || saving ? 0.6 : 1,
                    cursor: !canEdit || saving ? "not-allowed" : "pointer",
                  })}
                  disabled={!canEdit || saving}
                  onClick={saveSettings}
                >
                  {saving ? "Saving..." : "Save rules"}
                </button>
                <button
                  type="button"
                  style={button("secondary", { opacity: saving ? 0.6 : 1 })}
                  disabled={saving}
                  onClick={load}
                >
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <ConfirmDialog {...confirmDialog.props} />
    </div>
  );
};

export default PartnerAutoAssign;
